import { useCallback, useMemo } from 'react'
import { ReadSyncValidationsDocument } from 'generated/resources/graphql'
import { print } from 'graphql'

import { useLazyQuery } from 'modules/core/hooks/useQuery'

import { usePrivateIntegrationsSyncContext } from 'modules/integrations/hooks/SyncContext'
import { toSyncParameters } from 'modules/integrations/lib/parameters'
import {
  SyncIssue,
  useCompensationsSyncIssues,
} from 'modules/integrations/hooks/SyncContext/issues'

interface FetchIssuesResult {
  hasIssues: boolean
}

interface UseSyncIssuesLazyQueryResult {
  fetchIssues: () => Promise<FetchIssuesResult | undefined>
  issues: SyncIssue[]
  issuesLoading: boolean
  anyValidSyncable: boolean
}

export const useSyncIssuesLazyQuery = (): UseSyncIssuesLazyQueryResult => {
  const {
    syncEnabled,
    marketplaceIntegration,
    contextParameters,
    getDataCollectionSource,
    configuration,
    isBookkeeperView,
    companyId,
  } = usePrivateIntegrationsSyncContext()
  const [
    fetchSyncValidationsQuery,
    { data: syncValidationsData, loading: issuesLoading, networkActivity },
  ] = useLazyQuery(ReadSyncValidationsDocument)

  const validationNode = syncValidationsData?.integrations?.syncValidationsConnection.nodes[0]
  const rawIssues = useMemo(() => validationNode?.issues ?? [], [validationNode])
  const totalSyncables = validationNode?.totalSyncables ?? 0
  const invalidSyncables = validationNode?.invalidSyncables ?? 0
  const anyValidSyncable = invalidSyncables < totalSyncables

  const fetchIssues = useCallback(async (): Promise<FetchIssuesResult | undefined> => {
    if (!syncEnabled || !marketplaceIntegration || !companyId) return undefined

    const dataCollectionSource = getDataCollectionSource()
    const mappedVariables = dataCollectionSource?.mappedVariables
    const queryForSync = dataCollectionSource?.document
      ? print(dataCollectionSource.document)
      : null
    const graphqlVariablesJson =
      queryForSync && mappedVariables
        ? JSON.stringify({
            query: queryForSync,
            variables: { ...mappedVariables },
          })
        : undefined

    const result = await fetchSyncValidationsQuery({
      variables: {
        companyId,
        marketplaceIntegrationId: marketplaceIntegration.id,
        origin: configuration.origin,
        parameters: toSyncParameters(contextParameters),
        graphqlVariablesJson,
      },
      context: {
        isBookkeeperView,
      },
    })

    const node = result.data?.integrations?.syncValidationsConnection.nodes[0]
    const fetchedIssues = node?.issues ?? []

    return { hasIssues: fetchedIssues.length > 0 }
  }, [
    syncEnabled,
    marketplaceIntegration,
    companyId,
    getDataCollectionSource,
    configuration.origin,
    contextParameters,
    isBookkeeperView,
    fetchSyncValidationsQuery,
  ])

  return {
    fetchIssues,
    issues: useCompensationsSyncIssues(rawIssues, marketplaceIntegration),
    issuesLoading: networkActivity || issuesLoading,
    anyValidSyncable,
  }
}
